import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { ArrowRight, Target, MessageCircle, X } from 'lucide-react';

const StickyCTA = ({ title, buttons }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      
      // Show CTA when user has scrolled 50% of the page
      if (scrollPosition > (documentHeight - windowHeight) * 0.5) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleDismiss = () => {
    setIsDismissed(true);
    setIsVisible(false);
  };

  if (isDismissed || !isVisible) {
    return null;
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 animate-in slide-in-from-bottom-4 duration-500">
      {/* Background Overlay */}
      <div className="absolute inset-0 bg-background/95 backdrop-blur-sm border-t border-border/50"></div>
      
      {/* Content */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
          {/* Text Content */}
          <div className="text-center lg:text-left">
            <h3 className="text-xl md:text-2xl font-bold text-foreground mb-2">
              {title}
            </h3>
            <p className="text-muted-foreground">
              Ready to create your own success story? Let's discuss your project and achieve similar results.
            </p>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button 
              size="lg" 
              className="group bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              {buttons.primary}
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            
            <Button 
              variant="outline" 
              size="lg" 
              className="group border-primary/20 hover:bg-primary/5"
            >
              {buttons.secondary}
              <Target className="ml-2 h-5 w-5 group-hover:scale-110 transition-transform" />
            </Button>
            
            <Button 
              variant="ghost" 
              size="lg" 
              className="group text-primary hover:bg-primary/10"
            >
              {buttons.tertiary}
              <MessageCircle className="ml-2 h-5 w-5 group-hover:scale-110 transition-transform" />
            </Button>
          </div>

          {/* Dismiss Button */}
          <button
            onClick={handleDismiss}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-muted/50 transition-colors"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default StickyCTA;
